import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, Fuel, Settings2, Check, ArrowLeft, MapPin, Calendar, Info, MessageCircle, Phone, Share2, Facebook, Link2, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { useT } from "../i18n";
import { api } from "../lib/api";
import { useSettings } from "../lib/useSettings";

const fuelKey = (f) => ({ petrol: "fleet.petrol", diesel: "fleet.diesel", electric: "fleet.electric", hybrid: "fleet.hybrid" }[f] || f);

function computeDays(pickup, ret) {
  if (!pickup || !ret) return 0;
  const d1 = new Date(pickup), d2 = new Date(ret);
  const diff = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

export default function CarDetail() {
  const { carId } = useParams();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const s = useSettings();
  const [active, setActive] = useState(0);
  const [req, setReq] = useState({ location: "", pickup_date: "", return_date: "" });
  const [extras, setExtras] = useState([]);

  const { data: car, isLoading } = useQuery({ queryKey: ["car", carId], queryFn: async () => (await api.get(`/cars/${carId}`)).data });
  const { data: availability = [] } = useQuery({ queryKey: ["availability", carId], queryFn: async () => (await api.get(`/cars/${carId}/availability`)).data });

  const extrasList = s.extras || [];
  const days = useMemo(() => computeDays(req.pickup_date, req.return_date), [req]);
  const extrasTotal = useMemo(() => extras.reduce((sum, label) => sum + ((extrasList.find((e) => e.label === label)?.price || 0) * days), 0), [extras, days, extrasList]);
  const estimate = car && days > 0 ? car.price_per_day * days + extrasTotal : 0;

  if (isLoading || !car) return <div className="max-w-7xl mx-auto px-6 py-20 text-muted-foreground">{t("common.loading")}</div>;

  const toggleExtra = (label) => setExtras((prev) => prev.includes(label) ? prev.filter((k) => k !== label) : [...prev, label]);

  const buildMessage = () => {
    const fmt = (d) => new Date(d).toLocaleString(lang === "ro" ? "ro-RO" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    if (lang === "ro") {
      let m = `Bună! Vreau să rezerv ${car.name}`;
      if (req.location) m += ` (ridicare: ${req.location})`;
      if (days > 0) m += ` în perioada ${fmt(req.pickup_date)} - ${fmt(req.return_date)} (${days} zile)`;
      if (extras.length) m += `, opțiuni: ${extras.join(", ")}`;
      if (estimate > 0) m += `. Preț estimativ: ${estimate} lei.`;
      return m;
    }
    let m = `Hi! I'd like to book the ${car.name}`;
    if (req.location) m += ` (pickup: ${req.location})`;
    if (days > 0) m += ` for ${fmt(req.pickup_date)} - ${fmt(req.return_date)} (${days} days)`;
    if (extras.length) m += `, extras: ${extras.join(", ")}`;
    if (estimate > 0) m += `. Estimated price: ${estimate} lei.`;
    return m;
  };

  const canSend = req.location && req.pickup_date && req.return_date && days > 0;
  const waLink = s.whatsapp ? `https://wa.me/${s.whatsapp}?text=${encodeURIComponent(buildMessage())}` : "#";
  const emailSubject = lang === "ro" ? `Cerere rezervare: ${car.name}` : `Booking request: ${car.name}`;
  const mailLink = s.email ? `mailto:${s.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(buildMessage())}` : "#";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  const share = (network) => {
    const u = encodeURIComponent(pageUrl);
    const txt = encodeURIComponent(`${car.name} - ${s.company_name || "ClickRent"}`);
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      whatsapp: `https://wa.me/?text=${txt}%20${u}`,
      x: `https://twitter.com/intent/tweet?url=${u}&text=${txt}`,
    };
    if (network === "copy") { navigator.clipboard.writeText(pageUrl); toast.success(lang === "ro" ? "Link copiat" : "Link copied"); return; }
    window.open(links[network], "_blank");
  };

  const input = "border border-input bg-background px-3 py-2.5 text-sm rounded-md w-full focus:outline-none focus:ring-2 focus:ring-accent";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> {t("nav.fleet")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="aspect-[16/10] overflow-hidden rounded-lg border border-border bg-secondary mb-3">
            <img src={car.images?.[active]} alt={car.name} className="w-full h-full object-cover" data-testid="car-main-image" />
          </div>
          {car.images?.length > 1 && (
            <div className="flex gap-3">
              {car.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                  className={`w-24 h-20 rounded-md overflow-hidden border-2 ${active === i ? "border-accent" : "border-border"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{car.brand}</p>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{car.name}</h1>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => share("facebook")} className="p-2 border border-border rounded-md hover:bg-secondary transition-colors" data-testid="share-facebook"><Facebook className="h-4 w-4" /></button>
                <button onClick={() => share("whatsapp")} className="p-2 border border-border rounded-md hover:bg-secondary transition-colors" data-testid="share-whatsapp"><MessageCircle className="h-4 w-4" /></button>
                <button onClick={() => share("copy")} className="p-2 border border-border rounded-md hover:bg-secondary transition-colors" data-testid="share-copy"><Link2 className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6">
              {[
                { icon: Users, label: t("car.seats"), value: car.seats },
                { icon: Settings2, label: t("car.transmission"), value: t(car.transmission === "automatic" ? "fleet.automatic" : "fleet.manual") },
                { icon: Fuel, label: t("car.fuel"), value: t(fuelKey(car.fuel)) },
              ].map((sp, i) => (
                <div key={i} className="border border-border rounded-md p-4 text-center">
                  <sp.icon className="h-5 w-5 mx-auto text-accent mb-2" />
                  <p className="text-xs text-muted-foreground">{sp.label}</p>
                  <p className="font-semibold text-sm mt-0.5">{sp.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-8"><h2 className="font-heading font-semibold text-xl mb-3">{t("car.description")}</h2><p className="text-muted-foreground leading-relaxed">{car.description}</p></div>

            <div className="mt-8">
              <h2 className="font-heading font-semibold text-xl mb-3">{t("car.features")}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {car.features?.map((f) => <div key={f} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-accent shrink-0" /> {f}</div>)}
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[{ title: t("car.fuel_policy"), value: car.fuel_policy }, { title: t("car.mileage_policy"), value: car.mileage_policy }].map((p, i) => (
                <div key={i} className="bg-secondary rounded-md p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{p.title}</p>
                  <p className="text-sm">{p.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Request sidebar */}
        <aside className="lg:col-span-1">
          <div className="border border-border rounded-lg p-6 sticky top-20">
            <div className="flex items-end gap-1 mb-5">
              <span className="font-heading text-4xl font-bold">{car.price_per_day}</span>
              <span className="text-muted-foreground mb-1"> {t("common.currency")}{t("car.per_day")}</span>
            </div>

            <h3 className="font-heading font-semibold mb-3">{t("car.request_title")}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><MapPin className="h-3 w-3" /> {t("hero.pickup_location")}</label>
                <input className={input} data-testid="detail-location" placeholder={t("hero.location_placeholder")} value={req.location} onChange={(e) => setReq({ ...req, location: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><Calendar className="h-3 w-3" /> {t("hero.pickup_date")}</label>
                <input type="datetime-local" className={input} data-testid="detail-pickup-date" value={req.pickup_date} onChange={(e) => setReq({ ...req, pickup_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><Calendar className="h-3 w-3" /> {t("hero.return_date")}</label>
                <input type="datetime-local" className={input} data-testid="detail-return-date" value={req.return_date} onChange={(e) => setReq({ ...req, return_date: e.target.value })} />
              </div>
            </div>

            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("car.extras")}</p>
              <div className="space-y-2">
                {extrasList.map((ex, idx) => (
                  <button key={idx} onClick={() => toggleExtra(ex.label)} data-testid={`extra-${idx}`}
                    className={`w-full flex items-center justify-between border rounded-md px-3 py-2 text-left transition-colors ${extras.includes(ex.label) ? "border-accent bg-accent/5" : "border-border"}`}>
                    <span className="flex items-center gap-2 text-sm">
                      <span className={`w-4 h-4 rounded border flex items-center justify-center ${extras.includes(ex.label) ? "bg-accent border-accent" : "border-input"}`}>{extras.includes(ex.label) && <Check className="h-3 w-3 text-white" />}</span>
                      {ex.label}
                    </span>
                    <span className="text-xs font-semibold">{ex.price} lei/zi</span>
                  </button>
                ))}
              </div>
            </div>

            {estimate > 0 && (
              <div className="mt-5 bg-secondary rounded-md p-4 flex items-center justify-between" data-testid="estimate-box">
                <div>
                  <p className="text-xs text-muted-foreground">{t("car.estimated_price")} · {days} {t("car.days")}</p>
                  <p className="font-heading text-2xl font-bold" data-testid="estimate-total">{estimate} {t("common.currency")}</p>
                </div>
              </div>
            )}

            <a href={canSend ? waLink : undefined} target="_blank" rel="noreferrer" onClick={(e) => { if (!canSend) e.preventDefault(); }}>
              <Button className="w-full mt-4 rounded-md bg-[#25D366] hover:bg-[#20bd5a] h-11 gap-2" disabled={!canSend} data-testid="send-whatsapp-btn">
                <MessageCircle className="h-4 w-4" /> {canSend ? t("car.send_whatsapp") : t("car.select_dates")}
              </Button>
            </a>
            {s.phone && (
              <a href={`tel:${s.phone}`}>
                <Button variant="outline" className="w-full mt-2 rounded-md h-11 gap-2" data-testid="call-btn"><Phone className="h-4 w-4" /> {t("car.call_now")}</Button>
              </a>
            )}
            {s.email && (
              <a href={mailLink}>
                <Button variant="outline" className="w-full mt-2 rounded-md h-11 gap-2" data-testid="email-btn"><Mail className="h-4 w-4" /> {t("car.send_email")}</Button>
              </a>
            )}
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{t("car.request_note")}</p>

            {availability.length > 0 && (
              <div className="mt-6 pt-5 border-t border-border">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-3"><Info className="h-3 w-3" /> {t("car.unavailable_dates")}</p>
                <div className="space-y-1.5">
                  {availability.map((a, i) => (
                    <div key={i} className="text-xs bg-destructive/10 text-destructive rounded px-2 py-1.5">
                      {new Date(a.from).toLocaleDateString()} → {new Date(a.to).toLocaleDateString()}{a.label ? ` · ${a.label}` : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
